import axios, { AxiosRequestConfig, AxiosResponse, Canceler } from 'axios';
import { Alert, AlertProps, Message } from '@fx-ui/fine-design';
import {
    assign,
    get,
    getUUID,
    includes,
    getCookie,
    redirect,
    ECode,
    URL_PARAMETER
} from '@libs/shared';
import { globalState } from '../models';
import { getCsrf } from '../apis';
import { platformIntl, SITE_ROUTER } from '../constants';
import { getQueryUrl } from './site';

export const getErrorMsg = (error: { code?: number | string }) => {
    const code = get(error, 'code');
    const defaultErrorMsg = platformIntl.get('error.status.400');
    if (!code) {
        return defaultErrorMsg;
    }
    return platformIntl.get(`error.${code}`, {}, defaultErrorMsg);
};

const getCloudCsrf = () => {
    return getCookie('cloud_csrf')?.replace('s%3A', '').split('.')[0];
};

let cloudCsrfInitPromise: Promise<void> | null = null;

const ensureCloudCsrf = () => {
    if (cloudCsrfInitPromise) {
        return cloudCsrfInitPromise;
    }

    const curCloudCsrf = getCloudCsrf();
    if (curCloudCsrf && globalState.csrf) {
        if (curCloudCsrf !== globalState.csrf) {
            globalState.csrf = curCloudCsrf;
        }
        return Promise.resolve();
    }

    cloudCsrfInitPromise = new Promise((resolve, reject) => {
        getCsrf()
            .then(() => {
                // fix: getCsrf 响应成功后，document.cookie 有概率取不到最新值，延时获取
                setTimeout(() => {
                    globalState.csrf = getCloudCsrf();
                    cloudCsrfInitPromise = null;
                    resolve();
                }, 10);
            })
            .catch((error) => {
                cloudCsrfInitPromise = null;
                reject(error);
            });
    });

    return cloudCsrfInitPromise;
};

let refreshTokenPromise: Promise<void> | null = null;

const refreshToken = () => {
    if (refreshTokenPromise) {
        return refreshTokenPromise;
    }
    refreshTokenPromise = globalState.onRefreshToken!()
        .then((accessToken) => {
            refreshTokenPromise = null;
            globalState.setAccessToken(accessToken);
        })
        .catch((error) => {
            refreshTokenPromise = null;
            return Promise.reject(error);
        });
    return refreshTokenPromise;
};

export const post = <T = any>(
    url: string,
    data?: unknown,
    config?: {
        options?: AxiosRequestConfig<object>;
        cancelExecutor?: (c: Canceler) => void;
        cancelReject?: boolean;
        customHandleErrorCodes?: (number | string)[];
        customHandleHttpCodes?: number[];
    }
) => {
    return new Promise<AxiosResponse<T>>((resolve, reject) => {
        /**
         * 跨域且 accessToken 存在时，才需要添加 Authorization 请求头，避免平台误用子产品 AT
         * @see https://project.feishu.cn/b2rl2h/assignment/detail/7044227593
         */
        const needAuthorizationHeader = globalState.isCors && globalState.accessToken;

        const handleRequest = (csrfToken?: Record<string, string>) => {
            const headers = {
                'X-JDY-VER': globalState.version!,
                'X-Request-ID': getUUID()
            };
            if (needAuthorizationHeader) {
                assign(headers, {
                    Authorization: `Bearer ${globalState.accessToken}`
                });
            }
            if (csrfToken || globalState.csrf) {
                assign(
                    headers,
                    csrfToken
                        ? csrfToken
                        : {
                              'x-cloud-csrf-token': globalState.csrf
                          }
                );
            }
            assign(headers, globalState.reqHeaders);

            const axiosConfig = {
                ...config?.options,
                withCredentials: true,
                headers
            };

            if (config?.cancelExecutor) {
                assign(axiosConfig, {
                    cancelToken: new axios.CancelToken(config.cancelExecutor)
                });
            }

            axios
                .post(url, data, axiosConfig)
                .then((res) => resolve(res))
                .catch((error) => {
                    if (axios.isCancel(error)) {
                        if (config?.cancelReject) {
                            return reject(error);
                        }
                        return;
                    }
                    const httpCode = get(error, 'response.status');
                    const resData = get(error, 'response.data');
                    const errorCode = get(resData, 'code');

                    if (
                        httpCode === 401 &&
                        errorCode === ECode.SUITE_ACCESS_TOKEN_EXPIRED &&
                        globalState.onRefreshToken
                    ) {
                        refreshToken()
                            .then(() => {
                                post(url, data, config).then(resolve).catch(reject);
                            })
                            .catch(reject);
                        return;
                    }

                    if (includes(config?.customHandleHttpCodes, httpCode)) {
                        return reject(error);
                    }

                    if (includes(config?.customHandleErrorCodes, errorCode)) {
                        return reject(error);
                    }

                    switch (httpCode) {
                        case 400:
                            Message.warning(getErrorMsg({ code: errorCode }));
                            break;
                        case 401:
                            // 未登录
                            Message.warning(platformIntl.get('error.status.401'));
                            break;
                        case 402:
                            // 会话过期
                            Message.warning(platformIntl.get('error.status.402'));
                            break;
                        case 403:
                            // 没有数据请求权限
                            Message.warning(platformIntl.get('error.status.403'));
                            break;
                        case 404:
                            // 找不到数据资源
                            Message.warning(platformIntl.get('error.status.404'));
                            break;
                        case 409: {
                            const redirectToSignIn = () => {
                                redirect(
                                    getQueryUrl(
                                        [
                                            {
                                                key: URL_PARAMETER.REDIRECT_URI,
                                                value: encodeURIComponent(window.location.href)
                                            }
                                        ],
                                        SITE_ROUTER.AUTHENTICATION.SIGNOUT
                                    )
                                );
                            };
                            const showErrorAlert = (config: Partial<AlertProps>) => {
                                Alert.openAlert({
                                    type: 'error',
                                    text4Cancel: null,
                                    title: getErrorMsg(resData),
                                    onOk: redirectToSignIn,
                                    ...config
                                });
                            };

                            switch (errorCode) {
                                case ECode.USER_ALREADY_LOGGED_IN_ELSEWHERE:
                                case ECode.USER_SINGLE_DEVICE_LOGIN_COOKIE_EXPIRED:
                                    showErrorAlert({
                                        message: platformIntl.get('login.single.device.desc')
                                    });
                                    break;
                                case ECode.MEMBER_IS_FROZEN:
                                    // 当前成员被禁用
                                    showErrorAlert({
                                        message: platformIntl.get('error.tip.frozen')
                                    });
                                    break;
                                default:
                                    // 不在当前企业
                                    showErrorAlert({
                                        title: platformIntl.get('error.status.corpSwitch'),
                                        message: platformIntl.get('error.status.corpSwitch.tip'),
                                        text4Ok: platformIntl.get('iSee'),
                                        onOk: undefined
                                    });
                            }
                            break;
                        }
                        case 413:
                            // 请求实体过大
                            Message.warning(platformIntl.get('error.status.413'));
                            break;
                        case 429:
                            // 访问人数过多
                            Message.warning(platformIntl.get('error.status.429'));
                            break;
                        case 0:
                            // 请求中断
                            break;
                        default:
                            Message.add({
                                messageType: 'toast',
                                type: 'warning',
                                message: platformIntl.get('error.status.fail')
                            });
                            break;
                    }

                    return reject(error);
                });
        };

        if (needAuthorizationHeader) {
            handleRequest();
        } else if (globalState.csrfToken) {
            handleRequest(globalState.csrfToken);
        } else {
            ensureCloudCsrf()
                .then(() => {
                    handleRequest();
                })
                .catch((error) => {
                    reject(error);
                });
        }
    });
};
